import {
  TrackedShipmentEvent,
  TrackedShipmentPosition,
  TrackedShipmentState,
  TrackedShipmentStop,
} from '@project44-models/shipment-entity';

import { MARKER_ICON } from '../../components/RouteIcons/shipmentSegment';
import { scopedT } from '../helpers/scopedT';

const t = scopedT();

export type MapMarker = {
  lat: number;
  lng: number;
  type: MARKER_ICON;
  id: string;
  isCurrent: boolean;
};

export type Position = { lat: number; lon: number; dateTime: string };

export const mapStopsToMapMarkers = (
  stops: TrackedShipmentStop[],
  shouldDisplayCurrent: boolean,
  states: TrackedShipmentState[],
  events: TrackedShipmentEvent[],
  currentSegmentId?: string | null,
): MapMarker[] => {
  const coordinates: MapMarker[] = [];

  stops.forEach((stop) => {
    if (
      stop.location?.coordinates?.latitude &&
      stop.location?.coordinates?.longitude &&
      stop.id &&
      stop.type
    ) {
      const {
        id,
        location: {
          coordinates: { latitude: lat, longitude: lng },
        },
      } = stop;
      const type = getMarkerIconType(id, states, events);
      const isCurrent = id === currentSegmentId && !shouldDisplayCurrent;
      coordinates.push({ id, lat, lng, type, isCurrent });
    }
  });

  return coordinates;
};

export const getMarkerIconType = (
  id: string,
  states: TrackedShipmentState[],
  events: TrackedShipmentEvent[],
): MARKER_ICON => {
  if (!states.length) return MARKER_ICON.FUTURE_EVENT;
  const element = states.find(({ stopId }) => stopId === id);

  const hasPastStop = events
    .filter((event) => event.stopId === id)
    .some((event) => !!event.dateTime);

  if (hasPastStop) {
    return MARKER_ICON.PAST_EVENT;
  }

  if (!!element?.endDateTime && element?.type === 'AT_STOP') {
    return MARKER_ICON.PAST_EVENT;
  }

  return MARKER_ICON.FUTURE_EVENT;
};

export const shouldDisplayCurrentPosition = (
  states: TrackedShipmentState[],
): boolean => {
  if (!states.length) return false;
  const element = states[states.length - 1];
  const shouldNoDisplay =
    element?.type === 'AT_STOP' &&
    !!element?.startDateTime &&
    !element?.endDateTime;
  return !shouldNoDisplay;
};

export type StopType = Exclude<
  TrackedShipmentStop['type'],
  'UNKNOWN' | 'OTHER' | undefined
>;

const MARKER_LABEL: Record<StopType, string> = {
  DELIVERY: 'apiMapping.stop.delivery',
  DESTINATION: 'apiMapping.stop.destination',
  ORIGIN: 'apiMapping.stop.origin',
  PICKUP: 'apiMapping.stop.pickup',
  PORT_OF_DISCHARGE: 'apiMapping.stop.portOfDischarge',
  PORT_OF_LOADING: 'apiMapping.stop.portOfLoading',
  TRANSFER: 'apiMapping.stop.transfer',
  TRANSSHIPMENT_PORT: 'apiMapping.stop.transshipmentPort',
  RETURN: 'apiMapping.stop.return',
  WAREHOUSE: 'apiMapping.stop.warehouse',
  HUB: 'apiMapping.stop.hub',
  AIRPORT: 'apiMapping.stop.airport',
};

export const getMarkerLabel = (type: StopType): string => {
  return t(MARKER_LABEL[type]) || t('map.acquiringData');
};

// fix 180th meridian crossing
const adjustCoords = (
  origCoord: Omit<Position, 'dateTime'>,
  prevCoord: Omit<Position, 'dateTime'>,
) => {
  const adjustedCoord = {
    lat: origCoord.lat,
    lon: origCoord.lon,
  };
  if (prevCoord) {
    if (origCoord.lon - prevCoord?.lon >= 180) {
      adjustedCoord.lon -= 360;
    } else if (origCoord.lon - prevCoord?.lon <= -180) {
      adjustedCoord.lon += 360;
    }
  }
  prevCoord.lat = adjustedCoord.lat;
  prevCoord.lon = adjustedCoord.lon;

  return adjustedCoord;
};

export const getShipmentPositions = (
  shipmentPositions: TrackedShipmentPosition[],
  fixMeridianCrossing: boolean,
) => {
  const positions: Position[][] = [];
  if (shipmentPositions.length) {
    // valid routeSegmentIds can be a string or not provided, which is undefined in the response
    // we use a starting value of null so that even an undefined routeSegmentId will create an inner positions array
    let currentRouteSegmentId: string | undefined | null = null;
    shipmentPositions?.forEach(
      ({ latitude, longitude, routeSegmentId, dateTime }) => {
        const position = {
          dateTime: String(dateTime),
          lat: Number(latitude),
          lon: Number(longitude),
        };
        if (routeSegmentId !== currentRouteSegmentId) {
          positions.push([]);
          currentRouteSegmentId = routeSegmentId;
        }
        positions[positions.length - 1].push(position);
      },
    );
  }

  if (!fixMeridianCrossing) return positions;

  // fix 180th meridian crossing
  return positions?.map((segments) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const prevCoord: any = {};
    return segments.map(
      (origCoord: { lat: number; lon: number; dateTime: string }) => ({
        ...adjustCoords(origCoord, prevCoord),
        dateTime: origCoord.dateTime,
      }),
    );
  });
};

export const getShipmentRoutePositions = (
  shipmentPositions: TrackedShipmentPosition[],
): Position[] => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const prevCoord: any = {};
  return shipmentPositions?.map((origCoord) => {
    return {
      ...adjustCoords(
        { lat: Number(origCoord.latitude), lon: Number(origCoord.longitude) },
        prevCoord,
      ),
      dateTime: String(origCoord.dateTime),
    };
  });
};

export const adjustStopsCoords = (
  stops: TrackedShipmentStop[],
): TrackedShipmentStop[] => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const prevCoord: any = {};
  return stops.map((stop: TrackedShipmentStop) => {
    const coords = stop.location?.coordinates;
    if (!stop.location?.coordinates) return stop;
    if (!coords?.latitude || !coords?.longitude) return stop;

    const { lat: latitude, lon: longitude } = adjustCoords(
      { lat: coords.latitude, lon: coords.longitude },
      prevCoord,
    );
    stop.location.coordinates = { latitude, longitude };
    return stop;
  });
};

export const getStopMarkerLabel = (
  stop: TrackedShipmentStop,
  stopNumber: number,
) => {
  const t = scopedT();
  return stop.type && !['OTHER', 'UNKNOWN'].includes(stop.type)
    ? getMarkerLabel(stop.type as StopType)
    : t('stopNumber', { stopNumber });
};
